import React from 'react'
import { Button as MuiButton, type ButtonProps as MuiButtonProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { colors, spacing, typography } from '../../../tokens'

export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'ghost'
export type ButtonSize = 'small' | 'medium' | 'large'

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'size'> {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  loading?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
}

const StyledButton = styled(MuiButton, {
  shouldForwardProp: prop => !['customVariant', 'customSize', 'ownerState', 'theme', 'sx', 'as'].includes(String(prop)),
})<{ customVariant: ButtonVariant; customSize: ButtonSize }>(
  ({ customVariant, customSize }) => {
    const getVariantStyles = () => {
      switch (customVariant) {
        case 'primary':
          return {
            background: colors.accent.main,
            color: colors.accent.contrast,
            border: 'none',
            '&:hover': {
              background: colors.accent.hover,
            },
          }
        case 'secondary':
          return {
            background: 'transparent',
            color: colors.text.primary,
            border: `1px solid ${colors.border.primary}`,
            '&:hover': {
              backgroundColor: colors.background.cardHover,
              borderColor: colors.border.hover,
            },
          }
        case 'success':
          return {
            background: colors.success.main,
            color: '#FFFFFF',
            border: 'none',
            '&:hover': { background: colors.success.dark },
          }
        case 'warning':
          return {
            background: colors.warning.main,
            color: '#FFFFFF',
            border: 'none',
            '&:hover': { background: colors.warning.dark },
          }
        case 'danger':
          return {
            background: colors.error.main,
            color: '#FFFFFF',
            border: 'none',
            '&:hover': { background: colors.error.dark },
          }
        case 'ghost':
          return {
            background: 'transparent',
            color: colors.text.primary,
            border: 'none',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }
        default:
          return {}
      }
    }

    const getSizeStyles = () => {
      switch (customSize) {
        case 'small':
          return {
            padding: spacing.component.buttonPaddingSmall,
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.medium,
            minHeight: '32px',
          }
        case 'large':
          return {
            padding: spacing.component.buttonPaddingLarge,
            fontSize: typography.fontSize.lg,
            fontWeight: typography.fontWeight.semibold,
            minHeight: '48px',
          }
        case 'medium':
        default:
          return {
            padding: spacing.component.buttonPadding,
            fontSize: typography.scale.button.fontSize,
            fontWeight: typography.fontWeight.medium,
            minHeight: '40px',
          }
      }
    }

    return {
      borderRadius: spacing.borderRadius.button,
      textTransform: 'none',
      fontFamily: typography.fontFamily.primary,
      transition: `all ${spacing.transitions.fast} ${spacing.transitions.easing}`,
      boxShadow: 'none',
      '&:hover': { boxShadow: 'none' },
      '&:focus-visible': {
        outline: `2px solid ${colors.accent.main}`,
        outlineOffset: '2px',
      },
      '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
      },
      ...getVariantStyles(),
      ...getSizeStyles(),
    }
  }
)

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  children,
  loading = false,
  icon,
  iconPosition = 'left',
  disabled,
  ...props
}) => {
  const isDisabled = disabled || loading

  return (
    <StyledButton
      customVariant={variant}
      customSize={size}
      disabled={isDisabled}
      {...props}
    >
      {loading && <span>Loading...</span>}
      {!loading && icon && iconPosition === 'left' && (
        <span style={{ marginRight: spacing.scale[2], display: 'flex', alignItems: 'center' }}>{icon}</span>
      )}
      {!loading && children}
      {!loading && icon && iconPosition === 'right' && (
        <span style={{ marginLeft: spacing.scale[2], display: 'flex', alignItems: 'center' }}>{icon}</span>
      )}
    </StyledButton>
  )
}

export default Button
